/**
 * <volume-rewards> Custom Element
 *
 * 3-card volume-tier selector with up to TWO discount tiers (mid + high).
 * On user-initiated card click:
 *   - Updates the linked product form's quantity input (injects a hidden one if missing)
 *   - Silently applies the highest applicable discount code via GET /discount/<CODE>
 *     (no cart drawer pop-up — that would feel broken before ATC)
 *
 * Safety net: listens to cart:change. If line qty for this product hits a tier via the
 * cart drawer +/- buttons (not via card UI), applies the matching code with cart notification
 * so the drawer rerenders.
 *
 * The element handles arbitrary tier counts via data-mid-* / data-high-* attributes. If only
 * one tier is configured, it works like a single-tier selector. If both are configured, it
 * picks the highest qualifying tier on each interaction.
 */

class VolumeRewards extends HTMLElement {
  connectedCallback() {
    this.productId = parseInt(this.dataset.productId, 10);
    this.formId = this.dataset.formId;
    this.defaultQty = parseInt(this.dataset.defaultQuantity, 10) || 1;

    // Build tiers from data attributes, sorted ascending by quantity
    this.tiers = [];
    if (this.dataset.midQuantity && this.dataset.midDiscountCode) {
      this.tiers.push({
        quantity: parseInt(this.dataset.midQuantity, 10),
        code: this.dataset.midDiscountCode,
      });
    }
    if (this.dataset.highQuantity && this.dataset.highDiscountCode) {
      this.tiers.push({
        quantity: parseInt(this.dataset.highQuantity, 10),
        code: this.dataset.highDiscountCode,
      });
    }
    this.tiers.sort((a, b) => a.quantity - b.quantity);
    this.ourCodes = new Set(this.tiers.map((t) => t.code));

    this.form = this.formId ? document.getElementById(this.formId) : null;
    this.cards = Array.from(this.querySelectorAll('[data-volume-card]'));

    // Inject hidden quantity input if the buy_buttons block lacks one
    this.quantityInput = this.form ? this.form.querySelector('[name="quantity"]') : null;
    if (!this.quantityInput && this.form) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'quantity';
      hidden.value = '1';
      this.form.appendChild(hidden);
      this.quantityInput = hidden;
    }

    // Currently selected override (set when user picks a card with data-variant-override)
    this.activeOverride = null;

    // Card click + keyboard navigation
    this.cards.forEach((card) => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectCard(card, { silent: false });
      });
      card.addEventListener('keydown', (e) => this.handleKeydown(e, card));
    });

    // Intercept form submit if a variant-override card is active
    this._submitHandler = this.handleFormSubmit.bind(this);
    if (this.form) {
      this.form.addEventListener('submit', this._submitHandler, true);
    }

    // Safety net for users who reach the threshold via cart drawer +/- buttons
    this._cartChangeHandler = this.handleCartChange.bind(this);
    document.addEventListener('cart:change', this._cartChangeHandler);

    // Quantity picker multiplier (delegated, survives re-renders of the picker)
    this._multiplierHandler = this.handleMultiplierChange.bind(this);
    document.addEventListener('change', this._multiplierHandler);

    // Silently preselect default (no /discount fetch on page load)
    const defaultCard =
      this.cards.find((c) => parseInt(c.dataset.quantity, 10) === this.defaultQty) ||
      this.cards[Math.floor(this.cards.length / 2)] ||
      this.cards[0];
    if (defaultCard) {
      this.selectCard(defaultCard, { silent: true });
    }
  }

  disconnectedCallback() {
    if (this._cartChangeHandler) {
      document.removeEventListener('cart:change', this._cartChangeHandler);
    }
    if (this._submitHandler && this.form) {
      this.form.removeEventListener('submit', this._submitHandler, true);
    }
    if (this._multiplierHandler) {
      document.removeEventListener('change', this._multiplierHandler);
    }
  }

  /**
   * Find the highest tier whose quantity threshold is met by `qty`.
   * Returns null if no tier qualifies.
   */
  getApplicableTier(qty) {
    let result = null;
    for (const tier of this.tiers) {
      if (qty >= tier.quantity) result = tier;
    }
    return result;
  }

  selectCard(card, { silent }) {
    if (!card) return;
    const qty = parseInt(card.dataset.quantity, 10) || 1;
    const variantOverride = card.dataset.variantOverride
      ? parseInt(card.dataset.variantOverride, 10)
      : null;

    this.cards.forEach((c) => {
      const isSelected = c === card;
      c.dataset.selected = isSelected ? 'true' : 'false';
      c.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });

    // Track the selected tier + variant override; the final quantity is tier qty × quantity picker multiplier
    this.tierQuantity = qty;
    this.activeOverride = variantOverride ? { variantId: variantOverride, quantity: qty } : null;

    this.syncQuantity({ silent });
  }

  /**
   * The PDP quantity picker (if present) acts as a multiplier of the selected tier: tier 6 × 2 = 12 units.
   * It is looked up on each call because the theme may re-render the product info on variant change.
   */
  getMultiplier() {
    const input = this.formId
      ? document.querySelector(`[data-quantity-multiplier-for="${this.formId}"]`)
      : null;
    const value = input ? parseInt(input.value, 10) : 1;
    return Number.isNaN(value) || value < 1 ? 1 : value;
  }

  getTotalQuantity() {
    return (this.tierQuantity || 1) * this.getMultiplier();
  }

  syncQuantity({ silent }) {
    const total = this.getTotalQuantity();

    if (this.quantityInput) {
      this.quantityInput.value = String(total);
      this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // User-initiated AND a tier qualifies AND no override → silently attach the matching code
    // With override active we skip the discount because the target variant has its own pricing
    // (typically a bulk-pack with the discount already baked in)
    if (!silent && !this.activeOverride) {
      const tier = this.getApplicableTier(total);
      if (tier) {
        this.applyDiscountCode(tier.code, { notifyCart: false });
      }
    }
  }

  handleMultiplierChange(event) {
    const input = event.target;
    if (!input || !input.matches || !input.matches(`[data-quantity-multiplier-for="${this.formId}"]`)) return;
    this.syncQuantity({ silent: false });
  }

  async handleFormSubmit(event) {
    if (!this.activeOverride) return; // let the form submit normally

    event.preventDefault();
    event.stopPropagation();

    const override = this.activeOverride;
    const submitter = event.submitter || (this.form && this.form.querySelector('[type="submit"]:not([disabled])'));

    if (submitter && submitter.hasAttribute('aria-busy')) return;
    if (submitter) submitter.setAttribute('aria-busy', 'true');

    try {
      // Mirror the theme's ProductForm bundled-sections flow so the cart drawer
      // updates AND opens exactly like a normal ATC.
      const shopifyRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const formData = new FormData();
      formData.set('id', String(override.variantId));
      formData.set('quantity', String(override.quantity * this.getMultiplier()));
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', shopifyRoot + 'variants/' + override.variantId);

      const response = await fetch(shopifyRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error('cart add failed');
      const responseJson = await response.json();

      // If the shop is configured to use the cart page (not drawer), navigate there
      if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartType === 'page') {
        window.location.href = shopifyRoot + 'cart';
        return;
      }

      const cartResponse = await fetch(shopifyRoot + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cartContent = await cartResponse.json();
      cartContent.sections = responseJson.sections;

      // 1. variant:add on the form element — the cart drawer opens on this event
      this.form.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: Object.prototype.hasOwnProperty.call(responseJson, 'items') ? responseJson.items : [responseJson],
            cart: cartContent,
          },
        })
      );

      // 2. cart:change on documentElement — the cart drawer swaps its innerHTML from this
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent,
          },
        })
      );
    } catch (err) {
      console.warn('[volume-rewards] override add-to-cart failed', err);
      this.form.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: err && err.message ? err.message : 'unknown' },
        })
      );
    } finally {
      if (submitter) submitter.removeAttribute('aria-busy');
    }
  }

  async applyDiscountCode(code, { notifyCart }) {
    if (!code) return;

    try {
      const cartRes = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartRes.json();
      const existing = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];

      // Already on this exact code → no-op
      if (existing.some((d) => d && d.code === code)) return;

      // A foreign code (none of ours, but applicable) is active → don't overwrite
      // This protects customer-entered welcome codes etc.
      if (
        existing.some(
          (d) => d && d.code && !this.ourCodes.has(d.code) && d.applicable
        )
      ) {
        return;
      }

      await fetch('/discount/' + encodeURIComponent(code), {
        method: 'GET',
        redirect: 'manual',
        credentials: 'same-origin',
      });

      if (notifyCart) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { cart } })
        );
      }
    } catch (err) {
      console.warn('[volume-rewards] applyDiscountCode failed', err);
    }
  }

  handleCartChange(event) {
    const cart = event && event.detail && event.detail.cart;
    if (!cart || !Array.isArray(cart.items) || !this.tiers.length) return;

    const lineQty = cart.items
      .filter((item) => item.product_id === this.productId)
      .reduce((sum, item) => sum + (item.quantity || 0), 0);

    const tier = this.getApplicableTier(lineQty);
    if (tier) {
      this.applyDiscountCode(tier.code, { notifyCart: true });
    }
  }

  handleKeydown(event, card) {
    const idx = this.cards.indexOf(card);
    if (idx === -1) return;

    let nextIdx = idx;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIdx = (idx - 1 + this.cards.length) % this.cards.length;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        nextIdx = (idx + 1) % this.cards.length;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectCard(card, { silent: false });
        return;
      default:
        return;
    }

    event.preventDefault();
    const nextCard = this.cards[nextIdx];
    if (nextCard) {
      nextCard.focus();
      this.selectCard(nextCard, { silent: false });
    }
  }
}

if (!customElements.get('volume-rewards')) {
  customElements.define('volume-rewards', VolumeRewards);
}
